// components/CartDrawer.js

import {
	Drawer,
	DrawerOverlay,
	DrawerContent,
	DrawerCloseButton,
	DrawerHeader,
	DrawerBody,
	DrawerFooter,
	Button,
	Box,
	Text,
	HStack,
	IconButton,
	VStack,
	Divider,
	useToast,
	Avatar,
	Textarea,
	Collapse,
	Tooltip,
	useColorModeValue
} from '@chakra-ui/react';

import { FaTrash, FaChevronDown } from 'react-icons/fa';
import React, { useState, useEffect } from 'react';
import { useCart } from '../context/CartContext'
import PhotographerDetail from './PhotographerDetail2'
import dayjs from 'dayjs';
import axiosInstance from '../utils/axiosInstance';
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import CheckoutForm from "./CheckoutForm"; // your updated version



const calculateDurationInHours = (date, startTime, endTime) => {
	const start = dayjs(`${date} ${startTime}`, "ddd MMM D YYYY HH:mm");
	const end = dayjs(`${date} ${endTime}`, "ddd MMM D YYYY HH:mm");
	const duration = end.diff(start, 'minute') / 60;
	return Math.max(duration, 0); // Always positive
};

const formatDate = (dateString) => {
	return dayjs(dateString).format("ddd MMM D YYYY");
};


const CartDrawer = ({ isOpen, onClose }) => {
	const bgColor = useColorModeValue('white', 'black');
	const textColor = useColorModeValue('gray.800', 'white');
	const borderColor = useColorModeValue('gray.200', 'gray.700');
	const toast = useToast();
	const [selectedItem, setSelectedItem] = useState(null);
	const [viewMode, setViewMode] = useState("preview"); // could be 'preview', 'edit', etc.
	const [openItemId, setOpenItemId] = useState(null);
	const stripe_key =
		window.location.origin === 'https://www.papirazzi.app'
			? process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
			: process.env.REACT_APP_STRIPE_TEST_PUBLISHABLE_KEY;
	const stripePromise = loadStripe(stripe_key);
	const [clientSecret, setClientSecret] = useState(null);
	const [showCheckout, setShowCheckout] = useState(false);
	const [checkoutItems, setCheckoutItems] = useState([]);



	const {
		bookingDate,
		setBookingDate,
		startTime,
		setStartTime,
		endTime,
		setEndTime,
		description,
		setDescription,
	} = useCart();

	const handleCloseDrawer = () => {
		setShowCheckout(false);       // 👈 hide checkout form
		setClientSecret('');          // 👈 optional: reset stripe session
		onClose();                    // 👈 close drawer
	};

	const createPaymentIntent = async () => {
		try {
			const backendUrl =
				process.env.NODE_ENV === 'production'
					? process.env.REACT_APP_BACKEND_URL
					: 'http://localhost:8080';

			const token = localStorage.getItem('token');
			if (!token) {
				toast({
					title: 'Not logged in',
					description: 'Please log in before checking out.',
					status: 'error',
					duration: 3000,
					isClosable: true,
				});
				return;
			}

			// Map rich items
			const items = cartItems.map(item => {
				const details = item.photographer || item.videographer || item.model || item.studio || {};
				const rate = Number(details.price || 0); // USD
				const duration = Number(durationInHours || 0); // hours
				const item_total = rate * duration;
				const item_fee = item_total * 0.12; // 12% platform fee
				const item_payout = item_total - item_fee;
				const item_account_id = item.account_id;

				return {
					...details,
					item_total: item_total.toFixed(2),
					item_fee: item_fee.toFixed(2),
					item_payout: item_payout.toFixed(2),
					duration,
					item_account_id
				};
			});

			setCheckoutItems(items);

			const response = await axiosInstance.post(
				`${backendUrl}/create-payment-intent`,
				{
					description,
					total: total.toFixed(2), // Final amount with fees
					platform_fee: (platformFee + stripeFee).toFixed(2),
					stripe_fee: stripeFee,
					subtotal: subtotal.toFixed(2),
					duration: durationInHours,
					startTime,
					endTime,
					bookingDate,
					selectedDateFormatted,
					items
				},
				{
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${token}`,
					},
				}
			);

			if (response.data.clientSecret) {
				setClientSecret(response.data.clientSecret);
				setShowCheckout(true);
			}
		} catch (err) {
			console.error("Error creating payment intent:", err);
			toast({
				title: "Error",
				description: "Could not start checkout. Try again.",
				status: "error",
				duration: 3000,
				isClosable: true,
			});
		}
	};



	const generateTimeSlots = (date) => {
		const slots = [];
		const now = dayjs();
		const targetDate = dayjs(date).startOf("day");

		let currentTime;

		// If today, start 30 min from now (rounded to next 00 or 30)
		if (dayjs().isSame(targetDate, 'day')) {
			const roundedNow = now.add(30 - (now.minute() % 30), 'minute').startOf('minute');
			currentTime = roundedNow;
		} else {
			// Start from midnight for future dates
			currentTime = targetDate.hour(0).minute(0);
		}

		const endOfDay = targetDate.hour(23).minute(30);

		while (currentTime.isBefore(endOfDay)) {
			slots.push({
				value: currentTime.format("HH:mm"),         // ✅ Stored as military for backend/logic
				label: currentTime.format("h:mm A"),        // ✅ Displayed as 12-hour format
			});
			currentTime = currentTime.add(30, "minute");
		}

		return slots;
	};


	const getValidEndTimes = (start, slots) => {
		const index = slots.findIndex(t => t.value === start);
		return index !== -1 ? slots.slice(index + 2) : [];
	};


	const [availableStartTimes, setAvailableStartTimes] = useState([]);
	const [availableEndTimes, setAvailableEndTimes] = useState([]);


	useEffect(() => {
		if (bookingDate) {
			const generatedSlots = generateTimeSlots(bookingDate);
			setAvailableStartTimes(generatedSlots);

			// ✅ Don't override if already set (e.g. from localStorage)
			if (!startTime) {
				setStartTime(generatedSlots[0]?.value || '');
			}
			if (!endTime) {
				const endSlots = getValidEndTimes(generatedSlots[0]?.value, generatedSlots);
				setAvailableEndTimes(endSlots);
				setEndTime(endSlots[0]?.value || '');
			}
		}
	}, [bookingDate]);


	useEffect(() => {
		if (startTime && availableStartTimes.length) {
			const newEndSlots = getValidEndTimes(startTime, availableStartTimes);
			setAvailableEndTimes(newEndSlots);

			// ✅ Only reset endTime if current value is NOT in new list
			const isValid = newEndSlots.some(slot => slot.value === endTime);
			if (!isValid) {
				setEndTime(''); // ⛔ only reset if needed
			}
		}
	}, [startTime, availableStartTimes]);


	const { cartItems, setCartItems } = useCart();

	// Get Creator Type
	const getItemDetails = (item) => {
		return item.photographer || item.videographer || item.model || item.studio || {};
	};

	console.log(cartItems)
	const removeItem = id => {
		setCartItems(prev => prev.filter(item => item.id !== id));
		toast({
			title: 'Item removed.',
			status: 'info',
			duration: 2000,
			isClosable: true,
		});
	};


	// Get total duration and totals
	const selectedDateFormatted = bookingDate ? formatDate(bookingDate) : null;
	const durationInHours = bookingDate && startTime && endTime
		? calculateDurationInHours(selectedDateFormatted, startTime, endTime)
		: 0;

	const subtotal = cartItems.reduce((acc, item) => {
		const details = getItemDetails(item);
		return acc + (details.price * durationInHours);
	}, 0);

	const flatFee = 10; // $10 Flat fee
	const percentageFee = 0.1; // 10% Platform fee
	const platformFee = subtotal > 0 ? ((subtotal + flatFee) + subtotal * percentageFee) - subtotal : 0;
	const stripeFee = subtotal > 0 ? (subtotal + platformFee) * 0.029 + 0.3 : 0;
	const total = subtotal + platformFee + stripeFee;

	return (
		<Drawer isOpen={isOpen} placement="right" onClose={onClose} size="sm">
			<DrawerOverlay />
			<DrawerContent bg={bgColor} color={textColor}>
				<DrawerCloseButton
					onClick={() => {
						setShowCheckout(false); // 👈 Reset checkout state
						onClose(); // 👈 Also closes drawer
					}}
				/>
				<DrawerHeader borderBottom="1px" borderColor={borderColor}>
					Your Cart
				</DrawerHeader>

				<DrawerBody>
					{cartItems.length === 0 ? (
						<Text>No items in your cart.</Text>
					) : (
						<VStack spacing={4} align="stretch">

							{cartItems.map(item => {
								const details = getItemDetails(item);
								const fullName = `${details.first_name || ''} ${details.last_name || ''}`.trim();
								const label = item.photographer ? 'Photographer' : item.videographer ? 'Videographer' : item.model ? 'Model' : 'Studio';
								const isOpen = openItemId === item.id;

								return (
									<Box
										key={item.id}
										p={4}
										borderWidth="1px"
										borderRadius="md"
										borderColor={borderColor}
										bg={bgColor}
										w="full"
									>
										<VStack align="stretch" spacing={2}>
											<HStack justify="space-between" align="flex-start">
												<HStack spacing={4}>
													<Avatar
														size="md"
														name={fullName || details.name}
														src={details.avatar || details.avatarUrl || undefined}
													/>
													<Box>
														<Text fontWeight="bold" >{fullName || details.name}</Text>
														<Text fontSize="xs" color={textColor}>{label}</Text>
														<Text fontSize="xs" color={textColor}>
															Rate: ${details.price}/hr
														</Text>
													</Box>
												</HStack>

												<HStack spacing={2}>
													<IconButton
														icon={<FaChevronDown />}
														size="sm"
														variant="ghost"
														onClick={() => setOpenItemId(isOpen ? null : item.id)}
														aria-label="Toggle info"
													/>
													{/*
														<IconButton
															icon={<FaEdit />}
															size="sm"
															colorScheme="blue"
															onClick={() => {
																setSelectedItem(item);
																setViewMode("edit");
															}}
															aria-label="Edit item"
														/>*/}
													<IconButton
														icon={<FaTrash />}
														size="sm"
														colorScheme="red"
														onClick={() => removeItem(item.id)}
														aria-label="Remove item"
													/>
												</HStack>
											</HStack>

											{/* Dropdown Content */}
											<Collapse in={isOpen} animateOpacity>
												<Box p={2} pl={6} mt={2} bg={bgColor} borderRadius="md">
													<Text fontSize="sm" color={textColor}>
														<strong>Profile:</strong>{" "}
														<a
															href={`${window.location.origin}/user/${details.id}`}
															target="_blank"
															rel="noopener noreferrer"
															onClick={(e) => {
																e.preventDefault();
																const url = `${window.location.origin}/user/${details.id}`;

																// If PWA in standalone mode, open in in-app browser
																if (window.matchMedia('(display-mode: standalone)').matches) {
																	window.open(url, '_blank');
																} else {
																	// Normal browser behavior
																	window.open(url, '_blank', 'noopener,noreferrer');
																}
															}}
															style={{ textDecoration: 'underline', cursor: 'pointer' }}
														>
															View Profile
														</a>
													</Text>

												</Box>
											</Collapse>
										</VStack>
									</Box>
								);
							})}

							<Divider borderColor={borderColor} />
							{!showCheckout && (
								<>
									<VStack align="stretch" spacing={3}>
										<Box>
											<Text fontSize="sm" mb={1}>Date</Text>
											<input
												type="date"
												value={bookingDate}
												min={dayjs().format("YYYY-MM-DD")}
												onChange={(e) => setBookingDate(e.target.value)}
												style={{ padding: '8px', borderRadius: '6px', width: '100%' }}
											/>
										</Box>
										<HStack>
											<Box flex={1}>
												<Text fontSize="sm" mb={1}>Start Time</Text>
												<select
													value={startTime}
													onChange={(e) => setStartTime(e.target.value)}
													style={{ padding: '8px', borderRadius: '6px', width: '100%' }}
												>
													<option value="">Select</option>
													{availableStartTimes.map(({ value, label }) => (
														<option key={value} value={value}>{label}</option>
													))}
												</select>
											</Box>
											<Box flex={1}>
												<Text fontSize="sm" mb={1}>End Time</Text>
												<select
													value={endTime}
													onChange={(e) => setEndTime(e.target.value)}
													style={{ padding: '8px', borderRadius: '6px', width: '100%' }}
												>
													<option value="">Select</option>
													{availableEndTimes.map(({ value, label }) => (
														<option key={value} value={value}>{label}</option>
													))}
												</select>
											</Box>
										</HStack>
									</VStack>

									<Box>
										<Text fontSize="sm" mb={1}>Shoot Description</Text>
										<Textarea
											placeholder="Describe how you want the shoot to go..."
											value={description}
											onChange={(e) => setDescription(e.target.value)}
											size="sm"
											borderRadius="md"
											bg={bgColor}
										/>
									</Box>
								</>
							)}



							{/* Subtotal */}
							<Divider borderColor={borderColor} />
							<HStack justify="space-between" pt={2}>
								<Text fontWeight="bold">Duration:</Text>
								<Text>{durationInHours} hrs</Text>
							</HStack>

							<HStack justify="space-between">
								<Text fontWeight="bold">Subtotal:</Text>
								<Text>${subtotal.toFixed(2)}</Text>
							</HStack>


							{/* Platform + Stripe Fee */}
							<HStack justify="space-between">
								<Text fontWeight="medium">Platform Fee:</Text>
								<Text>${(platformFee + stripeFee).toFixed(2)}</Text>
							</HStack>

							<Divider borderColor={borderColor} />

							{/* Final Total */}
							<HStack justify="space-between">
								<Text fontWeight="bold">Total:</Text>
								<Text fontWeight="bold">${total.toFixed(2)}</Text>
							</HStack>
						</VStack>
					)}

					{/* Update/Edit View */}
					{viewMode === "edit" && selectedItem && (
						<PhotographerDetail
							photographer={(() => {
								const { startTime, endTime, from_date, ...rest } = selectedItem;
								return rest.photographer || rest.videographer || rest.model || rest.studio || {};
							})()}
							onClose={() => {
								setSelectedItem(null);
								setViewMode("preview");
							}}
							goBack={() => setViewMode("preview")}
						/>
					)}

					{showCheckout && clientSecret && (
						<>
							<Button
								variant="ghost"
								size="sm"
								leftIcon={<FaChevronDown />}
								onClick={() => setShowCheckout(false)}
							>
								Back to Edit
							</Button>
							<Elements stripe={stripePromise} options={{ clientSecret }}>
								<CheckoutForm
									bookingPayload={{
										description,
										duration: durationInHours,
										bookingDate,
										startTime,
										endTime,
										items: checkoutItems,
										description,
										total: total.toFixed(2), // Final amount with fees
										stripe_fee: stripeFee,
										platform_fee: (platformFee + stripeFee).toFixed(2),
										subtotal: subtotal.toFixed(2),
										selectedDateFormatted,
										comission: (total * 0.05).toFixed(2), // Assuming 5% commission
									}}
								/>
							</Elements>
						</>
					)}


				</DrawerBody>

				<DrawerFooter borderTop="1px" borderColor={borderColor}>
					<Button variant="outline" mr={3} onClick={handleCloseDrawer}>
						Close
					</Button>
					<Tooltip
						label="Please complete time, date, and description"
						isDisabled={
							bookingDate && startTime && endTime && description.trim()
						}
						hasArrow
					>
						<Button
							colorScheme="blue"
							isDisabled={
								cartItems.length === 0 ||
								!bookingDate ||
								!startTime ||
								!endTime ||
								!description.trim()
							}
							onClick={createPaymentIntent}
						>
							Checkout
						</Button>
					</Tooltip>



				</DrawerFooter>
			</DrawerContent>
		</Drawer>
	);
};

export default CartDrawer;
